<template>
  <div class="min-h-full subiekt-panel">
    <div class="mb-5 flex items-center justify-between">
      <SubiektBreadcrumb in-panel current="Ustawienia i kontrahenci" />
      <router-link to="/dashboard/subiekt/dokumenty" class="text-sm font-medium text-blue-500 hover:text-blue-700 md:hidden">&larr; Cofnij</router-link>
    </div>

    <div class="mb-4">
      <h1 class="text-lg font-medium text-gray-900">Ustawienia Subiekta</h1>
      <p class="mt-1 text-sm text-gray-500">Zmiany działają od razu, bez wgrywania nowej wersji systemu.</p>
    </div>

    <div class="select-none border-b border-gray-200 mb-6">
      <nav class="flex flex-wrap gap-2 pb-2" aria-label="Sekcje ustawień">
        <a v-for="tab in tabs" :key="tab.key" @click="activeTab = tab.key"
          :aria-current="activeTab === tab.key ? 'page' : undefined"
          :class="[activeTab === tab.key ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700',
                   'cursor-pointer rounded-md px-3 py-2 text-sm font-medium transition-all duration-300']">
          {{ tab.name }}
        </a>
      </nav>
    </div>

    <SubiektContractorsPanel v-if="activeTab === 'kontrahenci'" />

    <div v-else-if="loading" class="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <RefreshIcon class="h-8 w-8 animate-spin text-indigo-500" aria-hidden="true" />
      <p class="text-sm text-gray-500">Wczytuję ustawienia…</p>
    </div>

    <div v-else-if="config">
      <!-- Przełącznik wysyłki. Osobno i na górze, bo to jedyne ustawienie,
           które decyduje o tym, czy cokolwiek trafi do Subiekta naprawdę. -->
      <section class="rounded-xl px-5 py-4 mb-4" :class="config.enabled ? 'bg-red-50' : 'bg-gray-50'">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="min-w-0 flex-1">
            <h2 class="text-sm font-medium" :class="config.enabled ? 'text-red-900' : 'text-gray-900'">
              Wysyłka dokumentów do Subiekta
            </h2>
            <p class="mt-1 text-sm" :class="config.enabled ? 'text-red-800' : 'text-gray-600'">
              <template v-if="config.enabled">
                Włączona — przycisk „Wyślij" na dokumencie tworzy prawdziwy dokument w Subiekcie.
              </template>
              <template v-else>
                Wyłączona — dokumenty można oglądać i przygotowywać, ale nic nie opuści systemu.
              </template>
            </p>
          </div>
          <button @click="toggleEnabled()" :disabled="saving" type="button"
            :class="[saving ? 'cursor-not-allowed opacity-50' : '',
                     config.enabled ? 'bg-red-600 hover:bg-red-500' : 'bg-gray-900 hover:bg-gray-700',
                     'doc-press inline-flex items-center justify-center gap-x-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm']">
            {{ config.enabled ? 'Wyłącz wysyłkę' : 'Włącz wysyłkę' }}
          </button>
        </div>
      </section>

      <section class="rounded-xl bg-white ring-1 ring-gray-900 ring-opacity-5 shadow-sm px-5 py-4 mb-4">
        <h2 class="text-xs font-medium uppercase tracking-wide text-gray-500">Magazyn</h2>
        <div class="mt-3 max-w-xs">
          <label for="magazyn" class="block text-xs font-medium text-gray-600">
            Magazyn zapasowy (stary „główny")
          </label>
          <input id="magazyn" v-model.number="form.magazynGlownyId" type="number" min="1"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
          <p class="mt-1 text-xs text-gray-500">
            Używany tylko dopóki poniżej nie ustawisz Mianowic. Wtedy przestaje mieć znaczenie.
          </p>
        </div>

        <!-- Reguła klienta: przyjęcia od kontrahentów i pod faktury oraz wszystkie
             wydania idą na Mianowice. Produkcja ma własny łańcuch: rozchód półproduktów
             na magazynie produkcji, przyjęcie wewnętrzne na wyroby gotowe, a po zgodzeniu
             kwot przesunięcie na Mianowice. -->
        <p class="mt-4 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
          <span class="font-medium">Wydania i przyjęcia idą na Mianowice.</span>
          Produkcja: rozchód półproduktów z magazynu produkcji → przyjęcie wewnętrzne
          na wyroby gotowe → przesunięcie na Mianowice.
        </p>

        <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div v-for="pole in productionWarehouses" :key="pole.key">
            <label :for="pole.key" class="block text-xs font-medium text-gray-600">{{ pole.label }}</label>
            <input :id="pole.key" v-model.number="form[pole.key]" type="number" min="0"
              class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
            <!-- Nazwa spod wpisanego numeru. Bez niej łatwo wpisać 3 zamiast 4
                 i zorientować się dopiero po dokumencie na złym magazynie. -->
            <p v-if="warehouseName(form[pole.key])" class="mt-1 text-xs text-green-700">
              {{ warehouseName(form[pole.key]) }}
            </p>
            <p v-else-if="form[pole.key] > 0" class="mt-1 text-xs text-red-700">
              nie znam magazynu o tym numerze
            </p>
          </div>
        </div>
        <p class="mt-1 text-xs text-gray-500">
          Magazyny produkcyjne. Zostaw 0, dopóki nie znasz numeru — dokument dostanie wtedy blokadę
          zamiast trafić na zły magazyn.
        </p>
      </section>

      <!-- Jeden kafelek = jedna grupa. Wcześniej były dwie osobne listy (fraza→grupa
           i grupa→symbol), które trzeba było scalić w głowie — stąd „otto → OTTO_LUTZ"
           i „lutz → OTTO_LUTZ" wyglądały jak duplikat. -->
      <section class="mb-4">
        <h2 class="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Grupy sprzedaży</h2>
        <p class="text-sm text-gray-500 mb-3">
          Zamówienie trafia do pierwszej grupy, której fraza pasuje do jego kanału.
          Reszta ląduje w grupie zbiorczej na dole.
        </p>

        <div v-for="(grupa, index) in form.grupy" :key="index"
          class="rounded-xl bg-white ring-1 ring-gray-900 ring-opacity-5 shadow-sm px-5 py-4 mb-3">

          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <input v-model="grupa.nazwa" type="text" placeholder="OTTO + Lutz"
                class="w-full max-w-xs rounded-md border-gray-300 py-1.5 px-2 text-sm font-medium focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
              <p class="mt-1 text-xs text-gray-400">Nazwa pokazywana w panelu dokumentów</p>
            </div>
            <button v-if="!grupa.fallback" @click="form.grupy.splice(index, 1)" type="button"
              class="text-xs text-red-600 transition duration-200 hover:text-red-500">usuń grupę</button>
          </div>

          <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label class="block text-xs font-medium text-gray-600">Kontrahent na wydaniach (WZ)</label>
              <input v-model="grupa.symbol" type="text" maxlength="20" placeholder="OTTO-LUTZ"
                class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 font-mono text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
              <p class="mt-1 text-xs text-gray-400">Portal, na który mebel jedzie</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600">Kontrahent na zwrotach (PZ)</label>
              <input v-model="grupa.symbolZwrotu" type="text" maxlength="20" placeholder="AJE-MOBEL"
                class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 font-mono text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
              <p class="mt-1 text-xs text-gray-400">Spółka, która przyjmuje mebel z powrotem</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600">Dokument</label>
              <label class="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <input v-model="grupa.perTrasa" type="checkbox"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                osobny na każdą trasę
              </label>
              <p class="mt-1 text-xs text-gray-400">
                Wyłączone = jeden zbiorczy dokument na cały dzień
              </p>
            </div>
          </div>

          <div class="mt-4">
            <label class="block text-xs font-medium text-gray-600">
              {{ grupa.fallback ? 'Rozpoznawanie' : 'Rozpoznawane po frazach' }}
            </label>

            <p v-if="grupa.fallback" class="mt-1 text-sm text-gray-500">
              Wszystko, co nie pasuje do żadnej grupy powyżej — zamówienia ręczne, nowe portale,
              nierozpoznane kanały.
            </p>

            <div v-else class="mt-2 flex flex-wrap items-center gap-2">
              <span v-for="(fraza, fi) in grupa.frazy" :key="fi"
                class="inline-flex items-center gap-1.5 rounded-md bg-gray-100 py-1 pl-2.5 pr-1.5 text-sm text-gray-700">
                {{ fraza }}
                <button @click="grupa.frazy.splice(fi, 1)" type="button"
                  class="rounded text-gray-400 transition duration-200 hover:text-gray-700" aria-label="Usuń frazę">
                  <XIcon class="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
              <input v-model="grupa.nowaFraza" @keyup.enter="addPhrase(grupa)" type="text" placeholder="dopisz frazę i Enter"
                class="w-44 rounded-md border-gray-300 py-1 px-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
            </div>
            <p v-if="!grupa.fallback" class="mt-1 text-xs text-gray-400">
              Wystarczy, że kanał zamówienia zawiera którąkolwiek z fraz. Wielkość liter bez znaczenia.
            </p>
          </div>
        </div>

        <button @click="addGroup()" type="button"
          class="text-xs font-medium text-indigo-600 transition duration-200 hover:text-indigo-500">
          + dodaj grupę
        </button>
      </section>

      <section class="rounded-xl bg-white ring-1 ring-gray-900 ring-opacity-5 shadow-sm px-5 py-4 mb-6">
        <h2 class="text-xs font-medium uppercase tracking-wide text-gray-500">Rozpoznawanie zwrotów</h2>
        <p class="mt-1 text-sm text-gray-500">
          Lista PZ, której nazwa zawiera którąkolwiek z tych fraz, trafia do zakładki „Zwroty i reklamacje".
        </p>
        <input v-model="form.frazyZwrotowText" type="text" placeholder="zwrot, reklamac, serwis"
          class="mt-3 block w-full max-w-lg rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
        <p class="mt-1 text-xs text-gray-500">Po przecinku.</p>
      </section>

      <button @click="save()" :disabled="saving" type="button"
        :class="[saving ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-500',
                 'doc-press inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm']">
        <RefreshIcon v-if="saving" class="h-4 w-4 animate-spin" aria-hidden="true" />
        Zapisz ustawienia
      </button>
    </div>
  </div>
</template>

<script>
import { subiektPost } from '@/subiektApi';
import { RefreshIcon, XIcon } from '@heroicons/vue/solid';
import SubiektContractorsPanel from '@/components/SubiektContractorsPanel.vue';
import SubiektBreadcrumb from '@/components/SubiektBreadcrumb.vue';

// Grupa zbiorcza — łapie wszystko, co nie pasuje do pozostałych. Nie da się jej usunąć.
const FALLBACK = 'POZOSTALI';

export default {
  name: 'SubiektSettings',
  components: { RefreshIcon, XIcon, SubiektContractorsPanel, SubiektBreadcrumb },
  data() {
    return {
      activeTab: 'ogolne',
      tabs: [
        { key: 'ogolne', name: 'Ogólne' },
        { key: 'kontrahenci', name: 'Kontrahenci' },
      ],
      loading: true,
      saving: false,
      config: null,
      form: {
        magazynGlownyId: 1, magazynProdukcjaId: 0, magazynWyrobyGotoweId: 0, magazynMianowiceId: 0,
        grupy: [], frazyZwrotowText: '',
      },
      productionWarehouses: [
        { key: 'magazynProdukcjaId', label: 'Produkcja' },
        { key: 'magazynWyrobyGotoweId', label: 'Wyroby gotowe' },
        { key: 'magazynMianowiceId', label: 'Mianowice (wydania i przyjęcia)' },
      ],
    };
  },
  methods: {
    // Trzy mapy z bazy skladamy w jeden obiekt na grupę — użytkownik nie powinien
    // wiedzieć, że pod spodem to osobne słowniki.
    fillForm(config) {
      const keyNames = new Set([
        ...Object.values(config.kanaly || {}),
        ...Object.keys(config.kontrahenci || {}),
        ...Object.keys(config.nazwyGrup || {}),
      ]);
      keyNames.add(FALLBACK);

      const grupy = [...keyNames].map(key => ({
        key,
        nazwa: (config.nazwyGrup || {})[key] || key,
        symbol: (config.kontrahenci || {})[key] || '',
        symbolZwrotu: (config.kontrahenciZwrotow || {})[key] || '',
        perTrasa: (config.kanalyPerTrasa || []).includes(key),
        frazy: Object.entries(config.kanaly || {})
          .filter(([, groupEntry]) => groupEntry === key)
          .map(([fraza]) => fraza),
        nowaFraza: '',
        fallback: key === FALLBACK,
      }));

      // Grupa zbiorcza zawsze na końcu — to ona łapie resztę, więc jest ostatnia w kolejce.
      grupy.sort((a, b) => (a.fallback ? 1 : 0) - (b.fallback ? 1 : 0));

      this.form = {
        magazynGlownyId: config.magazynGlownyId,
        magazynProdukcjaId: config.magazynProdukcjaId || 0,
        magazynWyrobyGotoweId: config.magazynWyrobyGotoweId || 0,
        magazynMianowiceId: config.magazynMianowiceId || 0,
        grupy,
        frazyZwrotowText: (config.frazyZwrotow || []).join(', '),
      };
    },
    addPhrase(groupEntry) {
      const fraza = (groupEntry.nowaFraza || '').trim().toLowerCase();
      if(fraza.length === 0 || groupEntry.frazy.includes(fraza)) { groupEntry.nowaFraza = ''; return; }
      groupEntry.frazy.push(fraza);
      groupEntry.nowaFraza = '';
    },
    addGroup() {
      // Nowa grupa ląduje przed zbiorczą, bo tamta musi zostać ostatnia.
      const index = this.form.grupy.findIndex(g => g.fallback);
      const nowa = { key: '', nazwa: '', symbol: '', symbolZwrotu: '', perTrasa: false, frazy: [], nowaFraza: '', fallback: false };
      this.form.grupy.splice(index >= 0 ? index : this.form.grupy.length, 0, nowa);
    },
    buildPayload() {
      const channels = {};
      const kontrahenci = {};
      const kontrahenciZwrotow = {};
      const groupNames = {};
      const perRouteChannels = [];

      this.form.grupy.forEach(g => {
        // Klucz techniczny wyprowadzamy z nazwy tylko dla nowych grup — istniejące
        // zachowują swój, żeby nie zerwać powiązania z już wystawionymi dokumentami.
        const key = (g.key || '').trim() || g.nazwa.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');
        if(key.length === 0) return;
        if(g.nazwa.trim()) groupNames[key] = g.nazwa.trim();
        if(g.symbol.trim()) kontrahenci[key] = g.symbol.trim();
        if((g.symbolZwrotu || '').trim()) kontrahenciZwrotow[key] = g.symbolZwrotu.trim();
        if(g.perTrasa) perRouteChannels.push(key);
        (g.frazy || []).forEach(fraza => { if(fraza.trim()) channels[fraza.trim()] = key; });
      });

      // Mapa, z której skasowano ostatni wpis, przychodzi na backend jako pusty obiekt —
      // a taki jest tam celowo ignorowany, żeby stara przeglądarka nie wyczyściła
      // ustawień, których nie zna. Dlatego opróżnienie trzeba zgłosić wprost.
      const wyczysc = Object.entries({ kanaly: channels, kontrahenci, kontrahenciZwrotow, nazwyGrup: groupNames })
        .filter(([, mapa]) => Object.keys(mapa).length === 0)
        .map(([fieldName]) => fieldName);

      return {
        magazynGlownyId: this.form.magazynGlownyId,
        magazynProdukcjaId: this.form.magazynProdukcjaId,
        magazynWyrobyGotoweId: this.form.magazynWyrobyGotoweId,
        magazynMianowiceId: this.form.magazynMianowiceId,
        kanaly: channels, kontrahenci, kontrahenciZwrotow, nazwyGrup: groupNames, kanalyPerTrasa: perRouteChannels, wyczysc,
        frazyZwrotow: this.form.frazyZwrotowText.split(',').map(f => f.trim()).filter(f => f.length > 0),
        user: this.$store.state.userData,
      };
    },
    // Nazwy magazynów przychodzą z konfiguracji — most wypisuje je w logu przy
    // pierwszym połączeniu z bazą, więc listę da się zaktualizować bez zgadywania.
    warehouseName(docNumber) {
      if(!(docNumber > 0)) return '';
      return (this.config && this.config.nazwyMagazynow || {})[String(docNumber)] || '';
    },
    async load() {
      this.loading = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/config', {});
        if(!res.data.success) throw res.data.log || res.data.error;
        this.config = res.data.config;
        this.fillForm(this.config);
      } catch (error) {
        console.log(error);
        this.notify('Nie udało się wczytać ustawień.', error, false);
      } finally {
        this.loading = false;
      }
    },
    async save(extra = {}) {
      if(this.saving) return;
      this.saving = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/config/save', {
          ...this.buildPayload(), ...extra,
        });
        if(!res.data.success) throw res.data.log || res.data.error;
        this.config = res.data.config;
        this.fillForm(this.config);
        this.notify('Zapisano.', res.data.log, true);
      } catch (error) {
        console.log(error);
        this.notify('Nie udało się zapisać.', error, false);
      } finally {
        this.saving = false;
      }
    },
    // Włączenie wysyłki oznacza, że dokumenty pójdą do Subiekta naprawdę —
    // pytamy, zanim to się stanie.
    toggleEnabled() {
      const wlaczamy = !this.config.enabled;
      if(wlaczamy && !window.confirm('Od tej chwili przycisk „Wyślij" utworzy prawdziwy dokument w Subiekcie. Włączyć?')) return;
      this.save({ enabled: wlaczamy });
    },
    notify(head, error, success) {
      this.$store.commit('setNotification', {
        show: true, head,
        subheader: typeof error === 'string' ? error : (success ? '' : 'Coś poszło nie tak.'),
        success,
      });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
/* Tailwind 2 bez JIT nie generuje wartości dowolnych — docisk trzymamy tutaj. */
.doc-press {
  transition-property: transform, background-color, box-shadow;
  transition-duration: 200ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}
.doc-press:active {
  transform: scale(0.96);
}
.subiekt-panel {
  -webkit-font-smoothing: antialiased;
}
</style>
